import { useEffect } from 'react'
import { useScreenHeaderStore, type ScreenHeaderConfig } from '../stores/screenHeaderStore'

/**
 * Publishes this screen's content into the desktop screen-header bar
 * (Layout's <ScreenHeaderBar>). Pass `null` on mobile / while the screen has
 * nothing to show there — desktop widths only, mobile has its own pinned
 * chrome (out of scope for this bar).
 *
 * Callers must memoize `config` (useMemo) rather than passing a fresh object
 * every render — the effect re-publishes whenever it changes identity.
 * Clearing only happens on unmount (not on every config change), so
 * switching between two configs on the same mounted screen never flashes
 * the bar empty in between.
 */
export function useScreenHeader(config: ScreenHeaderConfig | null): void {
  const setHeader = useScreenHeaderStore((s) => s.setHeader)
  const clear = useScreenHeaderStore((s) => s.clear)

  useEffect(() => {
    if (config) setHeader(config)
    else clear()
  }, [config, setHeader, clear])

  useEffect(() => () => clear(), [clear])
}
